<template>
  <v-sheet class="py-2 px-5">
    <PersonField v-model="person" />
    <PeriodField v-model="period" :periods="periods" />
    <v-checkbox
      v-model="detailed"
      :label="$t('report.driver-performance.detailed')"
    />
    <v-checkbox
      v-model="daySummary"
      :label="$t('report.driver-performance.day-summary')"
    />
    <v-checkbox
      v-model="weekSummary"
      :label="$t('report.driver-performance.week-summary')"
    />
    <v-checkbox
      v-model="borderCrossing"
      :label="$t('report.driver-performance.border-crossing')"
    />
    <v-checkbox
      v-model="vehicleSummary"
      :label="$t('report.driver-performance.vehicle-summary')"
    />
    <v-checkbox
      v-model="showNote"
      :label="$t('report.driver-performance.show-note')"
    />
  </v-sheet>
</template>

<script>
import PeriodField from "@/components/fields/PeriodField";
import utils from "@/utils";
import PersonField from "@/components/fields/PersonField.vue";

export default {
  name: "DriverPerformanceReport",
  components: { PersonField, PeriodField },

  data() {
    return {
      periods: [
        { name: "month-current" },
        { name: "month-previous" },
        { name: "month-one" },
        { name: "month-range" },
        { name: "day-range" },
        { name: "day-rangetime" }
      ],
      enableFetch: false,
      fetchTimeout: null,

      person: null,
      period: null,
      detailed: true,
      daySummary: false,
      weekSummary: false,
      borderCrossing: false,
      vehicleSummary: false,
      showNote: false,
    };
  },

  methods: {
    fetchReport(wait = true) {
      if (this.enableFetch && this.person !== null && this.period !== null) {
        const payload = {
          name: "driver-performance",
          person: this.person,
          period: this.period,
          detailed: this.detailed,
          daySummary: this.daySummary,
          weekSummary: this.weekSummary,
          borderCrossing: this.borderCrossing,
          vehicleSummary: this.vehicleSummary,
          showNote: this.showNote,
          output: "pdf",
          download: false
        };

        if (this.fetchTimeout != null)
          clearTimeout(this.fetchTimeout);

        this.fetchTimeout = setTimeout(() => {
          this.$store.dispatch("report/fetch", payload)
            .then(response => {
              this.$emit("pdf", response.data);
            }).finally(() => this.fetchTimeout = null);
        }, (wait) ? 1000 : 1);
      }
    },

    downloadReport(output) {
      if (this.person !== null && this.period !== null)
        this.$store.dispatch("report/fetch", {
          name: "driver-performance",
          person: this.person,
          period: this.period,
          detailed: this.detailed,
          daySummary: this.daySummary,
          weekSummary: this.weekSummary,
          borderCrossing: this.borderCrossing,
          vehicleSummary: this.vehicleSummary,
          showNote: this.showNote,
          output: output,
          download: true
        });
    },

    updateUrl() {
      this.$router.replace({
        name: "report/driver-performance", query: {
          ...this.$route.query,
          personId: (this.person != null) ? this.person.personId : undefined,
          period: this.$router.periodToQuery(this.period),
          detailed: this.detailed,
          daySummary: this.daySummary,
          weekSummary: this.weekSummary,
          borderCrossing: this.borderCrossing,
          vehicleSummary: this.vehicleSummary,
          showNote: this.showNote,
        }
      }).catch(() => {
      });
    }
  },

  watch: {
    person: function() {
      this.fetchReport();
      this.$store.dispatch("report/setValue", { name: "personId", value: this.person });
    },

    period: function() {
      this.fetchReport();
      this.$store.dispatch("report/setValue", { name: "period", value: this.period });
      this.updateUrl();
    },

    detailed: function() {
      this.fetchReport();
      this.$store.dispatch("report/setValue", { name: "ridesDetailed", value: this.detailed });
      this.updateUrl();
    },

    daySummary: function() {
      this.fetchReport();
      this.$store.dispatch("report/setValue", { name: "ridesDaySummary", value: this.daySummary });
      this.updateUrl();
    },

    weekSummary: function() {
      this.fetchReport();
      this.$store.dispatch("report/setValue", { name: "ridesWeekSummary", value: this.weekSummary });
      this.updateUrl();
    },

    borderCrossing: function() {
      this.fetchReport();
      this.$store.dispatch("report/setValue", { name: "ridesBorderCrossing", value: this.borderCrossing });
      this.updateUrl();
    },

    vehicleSummary: function() {
      this.fetchReport();
      this.$store.dispatch("report/setValue", { name: "ridesVehicleSummary", value: this.vehicleSummary });
      this.updateUrl();
    },

    showNote: function() {
      this.fetchReport();
      this.$store.dispatch("report/setValue", { name: "ridesShowNote", value: this.showNote });
      this.updateUrl();
    }
  },

  mounted() {
    this.$emit("report",
      {
        name: "driver-performance",
        download: this.downloadReport
      }
    );

    this.person = this.$router.parseQueryPersonId(this.$route) || this.$store.state.report.values.personId || null;
    this.period = this.$router.parseQueryPeriod(this.$route) || this.$store.state.report.values.period || null;

    this.detailed = utils.notNullValueOtherNull(this.$router.parseQueryBool(this.$route, "detailed"), this.$store.state.report.values.ridesDetailed, true);
    this.daySummary = utils.notNullValueOtherNull(this.$router.parseQueryBool(this.$route, "daySummary"), this.$store.state.report.values.ridesDaySummary, false);
    this.weekSummary = utils.notNullValueOtherNull(this.$router.parseQueryBool(this.$route, "weekSummary"), this.$store.state.report.values.ridesWeekSummary, false);
    this.borderCrossing = utils.notNullValueOtherNull(this.$router.parseQueryBool(this.$route, "borderCrossing"), this.$store.state.report.values.ridesBorderCrossing, false);
    this.vehicleSummary = utils.notNullValueOtherNull(this.$router.parseQueryBool(this.$route, "vehicleSummary"), this.$store.state.report.values.ridesVehicleSummary, false);
    this.showNote = utils.notNullValueOtherNull(this.$router.parseQueryBool(this.$route, "showNote"), this.$store.state.report.values.ridesShowNote, false);

    setTimeout(() => {
      this.enableFetch = true;
      this.fetchReport(false);
    }, 1);
  }
};

</script>

<style scoped>

</style>